import { useCallback, useEffect, useRef, useState } from 'react';
import { LightbulbIcon } from '@phosphor-icons/react';
import { apiFetch } from '../../../auth/apiFetch';
import { pollWithWake } from '../../../data/kioskRefresh';

// Kitchen kiosk lights widget. Single master switch that controls all four
// Meross outlets (3 basement-strip channels + wine cabinet) together.
// Tap toggles every available light at once: if any are on, all go off;
// if all are off, all come on. Fires the HA service calls in parallel —
// HA's per-entity POST takes the entity_id directly, and we don't depend
// on HA having a pre-configured group.
//
// Excluded from this widget:
//   - `*_do_not_disturb` — controls the device's status LED, not the load
//   - `switch.basement_lamps_mss630_main_channel` — would cover only 3/4
//     lights (wine cabinet is a separate device), so we drive the strip
//     via its three sub-channels instead

const REFRESH_MS = 5 * 1000;
const POST_REFRESH_DELAY_MS = 1200;
// Vertical pointer travel (px) that distinguishes a swipe from a tap.
// Below this, the gesture falls through to the existing tap-toggle.
const SWIPE_THRESHOLD_PX = 24;

interface LightDef {
  entityId: string;
  fallbackLabel: string;
}

const LIGHTS: LightDef[] = [
  { entityId: 'light.overhead_light_1', fallbackLabel: 'Overhead Light 1' },
  { entityId: 'light.overhead_light_2', fallbackLabel: 'Overhead Light 2' },
  { entityId: 'light.overhead_light_3', fallbackLabel: 'Overhead Light 3' },
  { entityId: 'switch.large_desk_lamp', fallbackLabel: 'Large Desk Lamp' },
  { entityId: 'switch.small_desk_lamp', fallbackLabel: 'Small Desk Lamp' },
  { entityId: 'switch.large_leather_lamp', fallbackLabel: 'Large Leather Lamp' },
  { entityId: 'switch.small_leather_lamp', fallbackLabel: 'Small Leather Lamp' },
  { entityId: 'switch.wine_cabinet_lamp', fallbackLabel: 'Wine Cabinet' },
  { entityId: 'switch.bar_lights', fallbackLabel: 'Bar Lights' },
  { entityId: 'switch.record_player_light', fallbackLabel: 'Record Player Light' },
];

interface EntityState {
  entity_id: string;
  state: string; // 'on' | 'off' | 'unavailable'
  attributes: {
    friendly_name?: string;
  };
}

interface HaResponse {
  states: Record<string, EntityState>;
  stale: boolean;
}

interface LightsWidgetProps {
  className?: string;
}

export function LightsWidget({ className }: LightsWidgetProps = {}) {
  const [data, setData] = useState<HaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Optimistic overrides: entity_id → 'on' | 'off'. Cleared once the next
  // poll confirms the server agrees. Keeps the master toggle feeling
  // instant despite HA's eventual-consistency between service call and
  // state propagation.
  const [optimistic, setOptimistic] = useState<Record<string, 'on' | 'off'>>({});
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  // Pointer Y at swipe start. Cleared on pointerup/cancel. `justSwiped`
  // shorts out the click that follows a recognized swipe so the gesture
  // doesn't double-toggle.
  const swipeStartYRef = useRef<number | null>(null);
  const justSwipedRef = useRef(false);

  const entitiesParam = LIGHTS.map((l) => l.entityId).join(',');

  const fetchStates = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/ha?entities=${encodeURIComponent(entitiesParam)}`);
      if (!alive.current) return;
      if (!res.ok) {
        setError(`ha ${res.status}`);
        return;
      }
      const json = (await res.json()) as HaResponse;
      setData(json);
      setError(null);
      // Drop optimistic overrides that now match the server snapshot — the
      // server has caught up.
      setOptimistic((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const [id, want] of Object.entries(prev)) {
          if (json.states[id]?.state === want) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, [entitiesParam]);

  useEffect(() => {
    alive.current = true;
    void fetchStates();
    const stop = pollWithWake(() => void fetchStates(), REFRESH_MS);
    return () => {
      alive.current = false;
      stop();
    };
  }, [fetchStates]);

  if (!data) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-fg-secondary ${className ?? ''}`}
      >
        {error ?? 'Loading lights…'}
      </div>
    );
  }

  const tiles = LIGHTS.map((def) => {
    const entity = data.states[def.entityId];
    const serverState = entity?.state;
    const override = optimistic[def.entityId];
    const effectiveState = override ?? serverState;
    const isOn = effectiveState === 'on';
    const isUnavailable = !entity || serverState === 'unavailable';
    return { def, isOn, isUnavailable };
  });
  const available = tiles.filter((t) => !t.isUnavailable);
  const onCount = available.filter((t) => t.isOn).length;
  const anyOn = onCount > 0;
  const allUnavailable = available.length === 0;
  const masterDisabled = allUnavailable || pending;

  async function toggleAll() {
    if (masterDisabled) return;
    const want: 'on' | 'off' = anyOn ? 'off' : 'on';
    const service = anyOn ? 'turn_off' : 'turn_on';

    setOptimistic((prev) => {
      const next = { ...prev };
      for (const t of available) {
        next[t.def.entityId] = want;
      }
      return next;
    });
    setPending(true);

    try {
      const results = await Promise.allSettled(
        available.map((t) => {
          // HA domain is the prefix of the entity_id (e.g. `switch.foo` →
          // `switch`, `light.foo` → `light`). Lets the widget control both
          // Meross outlets (switch.*) and Leedarson bulbs (light.*) without
          // tagging each LightDef.
          const domain = t.def.entityId.split('.')[0];
          return apiFetch('/api/ha', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              domain,
              service,
              entity_id: t.def.entityId,
            }),
          }).then((res) => {
            if (!res.ok) throw new Error(`ha ${res.status}`);
            return res;
          });
        }),
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(`${failed} of ${available.length} failed`);
      } else {
        setError(null);
      }
      // Let the next poll reconcile; optimistic overrides will be cleared
      // by fetchStates as the server snapshot agrees.
      setTimeout(() => void fetchStates(), POST_REFRESH_DELAY_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'toggle failed');
    } finally {
      setPending(false);
    }
  }

  return (
    // No card chrome — the switch floats directly on the kiosk canvas, centered
    // in its grid slot next to the Ring card.
    <div
      className={`relative flex flex-col items-center justify-center overflow-hidden ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => {
          // Suppress the click that fires after a recognized swipe so a
          // single gesture doesn't toggle twice.
          if (justSwipedRef.current) {
            justSwipedRef.current = false;
            return;
          }
          void toggleAll();
        }}
        onPointerDown={(e) => {
          swipeStartYRef.current = e.clientY;
        }}
        onPointerUp={(e) => {
          const startY = swipeStartYRef.current;
          swipeStartYRef.current = null;
          if (startY === null) return;
          const dy = e.clientY - startY;
          if (Math.abs(dy) < SWIPE_THRESHOLD_PX) return;
          justSwipedRef.current = true;
          const wantOn = dy < 0; // swipe up = on, swipe down = off
          if (wantOn && !anyOn) void toggleAll();
          else if (!wantOn && anyOn) void toggleAll();
        }}
        onPointerCancel={() => {
          swipeStartYRef.current = null;
        }}
        disabled={masterDisabled}
        aria-pressed={anyOn}
        className={`w-full flex-1 flex flex-col items-center justify-center transition-opacity touch-none ${
          allUnavailable ? 'opacity-40 cursor-not-allowed' : ''
        } ${pending ? 'opacity-70' : ''}`}
      >
        {/* w-full → the pill fills the col-span-2 slot (= one launcher card's
            width; see the grid note in KioskShell). h-full → it fills the row
            height. The knob is always half the pill height (h-1/2) and is
            positioned by `top` rather than a fixed translate so it slides
            end-to-end whatever the pill height: on = top-2, off = pinned to
            the bottom (top = 50% − 0.5rem, leaving a matching 0.5rem gap). */}
        <div className="relative w-full h-full rounded-3xl bg-subtle border border-border">
          <div
            className={`absolute left-2 right-2 h-1/2 rounded-2xl flex items-center justify-center transition-all duration-300 ${
              anyOn
                ? 'top-2 bg-amber glow-amber'
                : 'top-[calc(50%_-_0.5rem)] bg-elevated'
            }`}
          >
            <LightbulbIcon
              size={32}
              weight="fill"
              style={{
                color: anyOn
                  ? 'var(--berg-text)'
                  : 'var(--berg-text-tertiary)',
                transition: 'color 300ms',
              }}
            />
          </div>
        </div>
      </button>
      {error && <div className="text-xs text-amber">{error}</div>}
    </div>
  );
}
